import { Box, Typography } from '@mui/material'
import {
    MenuButtonBold,
    MenuButtonBulletedList,
    MenuButtonItalic,
    MenuButtonOrderedList,
    MenuButtonUnderline,
    MenuControlsContainer,
    MenuDivider,
    MenuSelectHeading,
    RichTextEditor,
    type RichTextEditorRef,
} from 'mui-tiptap'
import { useEffect, useRef, useState } from 'react'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError, getPxAsRem } from 'shared/utils'
import { StyledRichTextEditor, ValueTypography } from './styled'

import { useMutation, useQuery } from '@apollo/client'
import Underline from '@tiptap/extension-underline'
import StarterKit from '@tiptap/starter-kit'
import {
    RosterProfileQuery,
    RosterProfileQueryVariables,
} from '__generated__/graphql'
import { format } from 'date-fns'
import type { FC } from 'react'
import { useParams } from 'react-router-dom'
import {
    GetRosterPitchNotes,
    UpdateRosterPitchNote,
} from 'recruiter/graphql/rosters.gql'
import DrawerComponent from 'shared/components/DrawerComponent'
import { ApolloQueryRefetchFunction } from 'shared/types/graphql'
import { getFullName } from 'shared/utils'

interface PitchNoteProps {
    open: boolean
    onClose: () => void
    data: RosterProfileQuery['roster']
    refetch: ApolloQueryRefetchFunction<
        RosterProfileQuery,
        RosterProfileQueryVariables
    >
}

const PitchNotes: FC<PitchNoteProps> = ({ open, onClose }) => {
    const { rosterId } = useParams()
    const { data, refetch } = useQuery(GetRosterPitchNotes, {
        variables: { rosterId: rosterId ?? '' },
    })
    const rteRef = useRef<RichTextEditorRef>(null)
    const toast = useToast()
    const [updatePitchNote] = useMutation(UpdateRosterPitchNote, {
        onCompleted: (data) => {
            if (data.updatePitchNote.success) {
                toast({
                    title: 'Success',
                    message:
                        data.updatePitchNote.message ??
                        'Pitch notes updated successfully',
                    severity: 'success',
                })
                refetch()
                onClose()
            } else {
                toast({
                    title: 'Error',
                    message:
                        data.updatePitchNote.message ??
                        'Failed to update pitch notes, please try again',
                    severity: 'error',
                })
            }
        },
        onError: (error) => {
            toast({
                title: 'Error',
                message: formatGraphQLError(
                    error,
                    'Failed to update pitch notes, please try again'
                ),
                severity: 'error',
            })
        },
    })

    const [noteContent, setNoteContent] = useState<string>('')
    const note = data?.roster?.pitchNote?.note

    useEffect(() => {
        setNoteContent(note || '')
    }, [note])

    useEffect(() => {
        if (rteRef.current && rteRef.current.editor) {
            rteRef.current.editor.commands.setContent(noteContent)
        }
    }, [noteContent])

    const handleCancel = () => {
        onClose()
    }

    const handleSave = async () => {
        const content = rteRef.current?.editor?.getHTML() || ''
        await updatePitchNote({
            variables: {
                rosterId: rosterId ?? '',
                pitchNote: content,
            },
        })
    }

    return (
        <DrawerComponent
            open={open}
            onPrimaryAction={handleSave}
            onClose={onClose}
            onCancel={handleCancel}
            primaryActionText="Save"
            hasCancelButton
            headerTitle="Pitch Notes"
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '80%',
                }}
            >
                <Box width={'40%'}>
                    <Typography variant="caption">CPI Contact</Typography>
                    <ValueTypography
                        variant="body2"
                        title={
                            data?.roster?.pitchNote?.createdByUser
                                ? getFullName(
                                      data?.roster?.pitchNote?.createdByUser,
                                      false
                                  )
                                : '-'
                        }
                    >
                        {data?.roster?.pitchNote?.createdByUser
                            ? getFullName(
                                  data?.roster?.pitchNote?.createdByUser,
                                  false
                              )
                            : '-'}
                    </ValueTypography>
                </Box>
                <Box>
                    <Typography variant="caption">Last Edited</Typography>
                    <Typography variant="body2">
                        {data?.roster?.pitchNote?.updatedAt
                            ? format(
                                  new Date(data.roster.pitchNote.updatedAt),
                                  'MM/dd/yyyy • HH:mm'
                              )
                            : '-'}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ marginTop: getPxAsRem(24) }}>
                <div>
                    <StyledRichTextEditor>
                        <RichTextEditor
                            ref={rteRef}
                            extensions={[StarterKit, Underline]}
                            content={noteContent ?? ''}
                            renderControls={() => (
                                <MenuControlsContainer>
                                    <MenuSelectHeading />
                                    <MenuDivider />
                                    <MenuButtonBold />
                                    <MenuButtonItalic />
                                    <MenuButtonUnderline />
                                    <MenuButtonBulletedList />
                                    <MenuButtonOrderedList />
                                </MenuControlsContainer>
                            )}
                        />
                    </StyledRichTextEditor>
                </div>
            </Box>
        </DrawerComponent>
    )
}

export default PitchNotes
